import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Save, CheckCircle, Star, TrendingUp, Target, Lightbulb, BookOpen, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { loadBragDataViaServiceRole, saveBragDataViaBypass } from '../utils/bragDreamBypass';
import { draftStorage, formatDraftAge } from '../utils/draftStorage';
import {
  getQuestionMetadata,
  getQuestionByIndex,
  getResponseKey,
  migrateLegacyResponses
} from '../utils/questionIndexMapper';

interface BragItem {
  id?: string;
  question: string;
  description: string;
  category: 'B' | 'R' | 'A' | 'G';
}

interface BragSheetData {
  [key: string]: string;
}

export const BRAG_QUESTIONS = {
  B: {
    title: 'Breakthrough Achievements',
    subtitle: 'Professional challenges and obstacles overcome',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-blue-500',
    questions: [
      "What's the most significant professional challenge you've overcome that had a major impact on your organization or career?",
      "Describe a time when you had to navigate a difficult workplace situation or conflict.",
      "Tell us about a project or initiative that seemed impossible at first but you made it happen.",
      "What's a major obstacle you faced in your career and how did you overcome it?",
      "Describe a situation where you had to step outside your comfort zone professionally."
    ]
  },
  R: {
    title: 'Remarkable Accomplishments',
    subtitle: 'Notable achievements and exceptional results',
    icon: <Star className="h-6 w-6" />,
    color: 'bg-yellow-500',
    questions: [
      "What's your most impressive professional achievement that exceeded expectations?",
      "Describe a time when you implemented an innovative solution that created lasting change.",
      "Tell us about a project where you delivered results that significantly surpassed the original goals.",
      "What's something remarkable you've accomplished that your colleagues still talk about?",
      "Describe your biggest 'wow' moment in your professional career."
    ]
  },
  A: {
    title: 'Actionable Experiences',
    subtitle: 'Practical applications of your expertise',
    icon: <Lightbulb className="h-6 w-6" />,
    color: 'bg-green-500',
    questions: [
      "Share an example of how you've applied your expertise to solve a complex problem.",
      "Describe a situation where your skills or knowledge made a direct, measurable impact.",
      "Tell us about a time when you identified and implemented a process improvement.",
      "What's a practical solution you've developed that others have adopted or replicated?",
      "Describe how you've used your unique skills to create value in your organization."
    ]
  },
  G: {
    title: 'Growth Highlights',
    subtitle: 'Professional development and evolution',
    icon: <TrendingUp className="h-6 w-6" />,
    color: 'bg-purple-500',
    questions: [
      "How have you evolved professionally over the past few years?",
      "Describe a skill you've developed that has significantly enhanced your career prospects.",
      "Tell us about a learning experience that changed your professional perspective.",
      "What's the most valuable feedback you've received and how did you act on it?",
      "Describe how you've grown as a leader or team member."
    ]
  }
};

interface BragSheetProps {
  onComplete?: () => void;
}

const BragSheet: React.FC<BragSheetProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState<'B' | 'R' | 'A' | 'G'>('B');
  const [responses, setResponses] = useState<BragSheetData>({});
  const [isSaving, setIsSaving] = useState(false);
  const [bragSheetId, setBragSheetId] = useState<string | null>(null);
  const [savedSections, setSavedSections] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const loadingRef = useRef(false);

  // Add bypass mode for testing
  const [bypassMode, setBypassMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const responsesRef = useRef(responses);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    responsesRef.current = responses;
  }, [responses]);

  const saveDraft = useCallback(() => {
    if (!user?.id) return;
    draftStorage.save('brag_sheet', responsesRef.current, user.id);
  }, [user?.id]);

  const loadDraft = useCallback(() => {
    if (!user?.id) return null;
    return draftStorage.load('brag_sheet', user.id);
  }, [user?.id]);

  const clearDraft = useCallback(() => {
    if (!user?.id) return;
    draftStorage.remove('brag_sheet', user.id);
  }, [user?.id]);

  useEffect(() => {
    saveDraft();

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [responses, saveDraft]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 BragSheet: Tab hidden, saving draft...');
        saveDraft();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveDraft]);

  // Memoized loading function to prevent multiple calls
  const loadBragSheet = useCallback(async () => {
    if (!user?.id || hasLoadedRef.current || loadingRef.current) {
      console.log('BragSheet: Skipping load - user:', !!user?.id, 'hasLoaded:', hasLoadedRef.current, 'loading:', loadingRef.current);
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setLoadError(null);

    try {
      console.log('BragSheet: Loading BRAG sheet for user:', user.id);

      // Load BRAG sheet directly from database
      console.log('🔍 BragSheet: Loading data from database...');

      // Get existing BRAG sheet for this user - use better error handling
      const { data: bragSheets, error: sheetsError, count } = await supabase
        .from('brag_sheets')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .limit(1);

      if (sheetsError) {
        console.error('BragSheet: Error loading sheets:', sheetsError);
        throw new Error(`Failed to load BRAG sheet: ${sheetsError.message}`);
      }

      const bragSheetData = bragSheets && bragSheets.length > 0 ? bragSheets[0] : null;

      // Get all items for this sheet if it exists
      let bragItemsData: any[] = [];
      if (bragSheetData) {
        const { data: items, error: itemsError } = await supabase
          .from('brag_items')
          .select('*')
          .eq('brag_sheet_id', bragSheetData.id);

        if (itemsError) {
          console.error('BragSheet: Error loading items:', itemsError);
          throw new Error(`Failed to load BRAG items: ${itemsError.message}`);
        }

        bragItemsData = items || [];
      }

      console.log('🔍 BragSheet: Loaded', bragItemsData.length, 'items');

      if (bragSheetData) {
        setBragSheetId(bragSheetData.id);
        console.log('✅ BragSheet: Found existing sheet:', bragSheetData.id);
      } else {
        // Create new sheet if none exists
        console.log('🆕 BragSheet: Creating new sheet...');
        const { data: newSheet, error: createError } = await supabase
          .from('brag_sheets')
          .insert({ user_id: user.id })
          .select('id')
          .single();

        if (createError) {
          console.error('BragSheet: Error creating sheet:', createError);
          throw new Error(`Failed to create BRAG sheet: ${createError.message || 'Unknown error'}`);
        }

        if (!newSheet || !newSheet.id) {
          console.error('BragSheet: Sheet created but no ID returned');
          throw new Error('Failed to create BRAG sheet: No ID returned');
        }

        // Verify the sheet was actually created by querying it back
        const { data: verifySheet, error: verifyError } = await supabase
          .from('brag_sheets')
          .select('id')
          .eq('id', newSheet.id)
          .single();

        if (verifyError || !verifySheet) {
          console.error('BragSheet: Sheet creation verification failed:', verifyError);
          throw new Error('Failed to verify BRAG sheet creation. Please try again.');
        }

        setBragSheetId(newSheet.id);
        console.log('✅ BragSheet: Created and verified new sheet:', newSheet.id);
      }

      // Process the items data
      console.log('📊 BragSheet: Processing', bragItemsData.length, 'items');

      const loadedResponses: BragSheetData = {};
      const savedSects = new Set<string>();

      bragItemsData.forEach((item: any) => {
        if (item.question && item.description) {
          // Try to get metadata for this question
          const metadata = getQuestionMetadata(item.question, BRAG_QUESTIONS);

          if (metadata) {
            // Use stable key based on category and index
            const key = getResponseKey(metadata.category, metadata.index);
            loadedResponses[key] = item.description;
            savedSects.add(metadata.category);
            console.log('📝 BragSheet: Loaded response for:', metadata.category, 'index', metadata.index, '->', item.question.substring(0, 50) + '...');
          } else {
            // Legacy question - try to match it or keep as-is
            console.warn('⚠️ BragSheet: Could not match question to current set:', item.question.substring(0, 50) + '...');
            // Store with original key for now - will be migrated on save
            loadedResponses[item.question] = item.description;
          }
        }
      });

      // Migrate any legacy keys to stable keys
      const migratedResponses = migrateLegacyResponses(loadedResponses, BRAG_QUESTIONS);

      const draft = loadDraft();
      if (draft && Object.keys(draft).length > Object.keys(migratedResponses).length) {
        const draftAge = draftStorage.getDraftAge('brag_sheet', user.id);
        if (draftAge && draftAge < 7 * 24 * 60 * 60 * 1000) {
          console.log('📝 BragSheet: Restoring draft from', formatDraftAge(draftAge));
          const migratedDraft = migrateLegacyResponses(draft, BRAG_QUESTIONS);
          setResponses(migratedDraft);
          toast.info(`Draft restored with ${Object.keys(migratedDraft).length} responses from ${formatDraftAge(draftAge)}`);
          hasLoadedRef.current = true;
          return;
        }
      }

      setResponses(migratedResponses);
      setSavedSections(savedSects);
      hasLoadedRef.current = true;

      console.log('✅ BragSheet: Final state - Responses:', Object.keys(migratedResponses).length, 'Saved sections:', Array.from(savedSects));

      if (Object.keys(migratedResponses).length > 0) {
        toast.success(`Loaded ${Object.keys(migratedResponses).length} BRAG responses from database`);
      }
    } catch (error) {
      console.error('BragSheet: Error loading data:', error);
      setLoadError(error.message || 'Failed to load BRAG sheet data');
      toast.error(`Failed to load BRAG data: ${error.message}`);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [user?.id]);

  // Load existing BRAG sheet data
  useEffect(() => {
    if (!user?.id || hasLoadedRef.current || loadingRef.current) return;

    loadBragSheet();
  }, [user?.id, loadBragSheet]);

  const handleResponseChange = (question: string, value: string, category: string, index: number) => {
    // Use stable key instead of question text
    const key = getResponseKey(category, index);
    setResponses(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSection = async (section: 'B' | 'R' | 'A' | 'G') => {
    if (!user || !bragSheetId) {
      console.error('BragSheet: Cannot save - missing user or sheet ID', { user: !!user, bragSheetId });
      toast.error('Unable to save: Sheet not properly initialized. Please refresh the page and try again.');
      return;
    }

    setIsSaving(true);

    try {
      console.log('BragSheet: Saving section:', section, 'for sheet:', bragSheetId);

      // Pre-flight check: Verify the sheet still exists in database
      const { data: sheetCheck, error: checkError } = await supabase
        .from('brag_sheets')
        .select('id')
        .eq('id', bragSheetId)
        .single();

      if (checkError || !sheetCheck) {
        console.error('BragSheet: Sheet verification failed:', checkError);
        throw new Error('Your BRAG sheet could not be found. Please refresh the page.');
      }

      const sectionQuestions = BRAG_QUESTIONS[section].questions;
      const sectionResponses = sectionQuestions
        .map((question, index) => {
          const key = getResponseKey(section, index);
          const response = responses[key];
          return response && response.trim().length >= 10 ? {
            question,
            description: response,
            category: section,
            index
          } : null;
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      console.log('BragSheet: Section responses to save:', sectionResponses.length, 'items');

      // Delete existing items for this section
      const { error: deleteError } = await supabase
        .from('brag_items')
        .delete()
        .eq('brag_sheet_id', bragSheetId)
        .in('question', sectionQuestions);

      if (deleteError) {
        console.error('BragSheet: Error deleting existing items:', deleteError);
        throw new Error(`Failed to delete existing items: ${deleteError.message}`);
      }

      // Insert new responses for this section
      if (sectionResponses.length > 0) {
        const itemsToInsert = sectionResponses.map(response => ({
          brag_sheet_id: bragSheetId,
          question: response.question,
          description: response.description
        }));

        console.log('BragSheet: Inserting items:', itemsToInsert);

        const { data: insertedData, error: insertError } = await supabase
          .from('brag_items')
          .insert(itemsToInsert)
          .select('id');

        if (insertError) {
          console.error('BragSheet: Insert error:', insertError);
          throw new Error(`Failed to save responses: ${insertError.message}`);
        }

        if (!insertedData || insertedData.length === 0) {
          console.warn('BragSheet: No data returned from insert');
          throw new Error('Save operation did not return confirmation. Please verify your data was saved.');
        }

        console.log('BragSheet: Successfully inserted', insertedData.length, 'items');
      } else {
        console.log('BragSheet: No responses to save for section:', section);
      }

      setSavedSections(prev => new Set([...prev, section]));
      saveDraft();
      toast.success(`${BRAG_QUESTIONS[section].title} section saved successfully! (${sectionResponses.length} responses)`);
    } catch (error: any) {
      console.error('BragSheet: Error saving section:', error);
      const errorMessage = error?.message || 'An unknown error occurred';
      toast.error(`Failed to save section: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const saveAllSections = async () => {
    for (const section of ['B', 'R', 'A', 'G'] as const) {
      await saveSection(section);
    }
    
    if (onComplete) {
      onComplete();
    }
  };

  const getCompletionPercentage = () => {
    const totalQuestions = Object.values(BRAG_QUESTIONS).reduce((sum, section) => sum + section.questions.length, 0);
    // Only count responses with meaningful content (>= 10 characters)
    let answeredQuestions = 0;
    Object.entries(BRAG_QUESTIONS).forEach(([category, config]) => {
      config.questions.forEach((_, index) => {
        const key = getResponseKey(category, index);
        if (responses[key] && responses[key].trim().length >= 10) {
          answeredQuestions++;
        }
      });
    });
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const getSectionCompletion = (section: 'B' | 'R' | 'A' | 'G') => {
    const sectionQuestions = BRAG_QUESTIONS[section].questions;
    // Only count responses with meaningful content (>= 10 characters)
    const answeredQuestions = sectionQuestions.filter((_, index) => {
      const key = getResponseKey(section, index);
      return responses[key] && responses[key].trim().length >= 10;
    }).length;
    return Math.round((answeredQuestions / sectionQuestions.length) * 100);
  };

  const sections = Object.keys(BRAG_QUESTIONS) as Array<'B' | 'R' | 'A' | 'G'>;
  const currentIndex = sections.indexOf(currentSection);
  const currentSectionData = BRAG_QUESTIONS[currentSection];

  // Retry function - defined at top level before any conditional returns
  const retryLoad = useCallback(() => {
    hasLoadedRef.current = false;
    loadingRef.current = false;
    setLoadError(null);
    setIsLoading(true);
    // Don't call loadBragSheet directly - let the useEffect handle it
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto w-full overflow-x-hidden">
        <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your BRAG sheet...</p>
          <Button 
            onClick={async () => {
              const { data, error } = await supabase.functions.invoke('debug-brag-dream-access', {
                body: { userId: user?.id }
              });
              console.log('Manual debug results:', data);
              setDebugInfo(data);
            }}
            variant="outline"
            size="sm"
            className="mt-4"
          >
            Run Debug
          </Button>
        </div>
      </div>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto w-full overflow-x-hidden">
        <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
          <div className="bg-red-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading BRAG Sheet</h3>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <div className="space-y-2">
            <Button onClick={retryLoad}>
              Try Again
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
            <Button
              onClick={async () => {
                const { data, error } = await supabase.functions.invoke('debug-brag-dream-access', {
                  body: { userId: user?.id }
                });
                console.log('Debug results:', data);
                setDebugInfo(data);
                if (data?.fixes?.length > 0) {
                  toast.success('Applied fixes, refreshing...');
                  setTimeout(() => window.location.reload(), 2000);
                }
              }}
              variant="outline"
            >
              Debug & Fix
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto w-full overflow-x-hidden">
      {/* Course Banner */}
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white">
            <Video className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">
              Need Help Getting Started?
            </h3>
            <p className="text-sm text-blue-800 mb-4">
              We recommend completing our <strong>Career Foundation Course</strong> (6 videos, 42 minutes)
              before filling out your BRAG sheet. You'll learn powerful techniques for recalling achievements,
              translating them to value, and using the STAR method.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/course')}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Take the Course
              </Button>
              <Button
                onClick={() => navigate('/user-guide')}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                View All Resources
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Header */}
      <div className="bg-white rounded-xl border border-border/40 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">BRAG Sheet</h2>
          <div className="text-sm text-muted-foreground">
            Overall Progress: {Object.keys(responses).filter(key => responses[key] && responses[key].trim().length >= 10).length} of {Object.values(BRAG_QUESTIONS).reduce((sum, section) => sum + section.questions.length, 0)} questions ({getCompletionPercentage()}% Complete)
          </div>
        </div>
        
        {/* Section Tabs */}
        <div className="flex flex-nowrap overflow-x-auto space-x-1 bg-gray-100 rounded-lg p-1">
          {sections.map((section) => {
            const sectionData = BRAG_QUESTIONS[section];
            const completion = getSectionCompletion(section);
            const isSaved = savedSections.has(section);
            
            return (
              <button
                key={section}
                onClick={() => setCurrentSection(section)}
                className={`flex-shrink-0 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  currentSection === section
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className={`p-1 rounded-full ${sectionData.color} text-white`}>
                  {React.cloneElement(sectionData.icon, { className: 'h-4 w-4' })}
                </div>
                <span className="hidden sm:inline">{sectionData.title}</span>
                <span className="sm:hidden">{section}</span>
                {completion > 0 && (
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                    {completion}%
                  </span>
                )}
                {isSaved && <CheckCircle className="h-4 w-4 text-green-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Section Content */}
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className={`p-3 rounded-full ${currentSectionData.color} text-white`}>
            {currentSectionData.icon}
          </div>
          <div>
            <h3 className="text-2xl font-bold">{currentSectionData.title}</h3>
            <p className="text-muted-foreground">{currentSectionData.subtitle}</p>
          </div>
        </div>

        <div className="space-y-6">
          {currentSectionData.questions.map((question, index) => {
            const key = getResponseKey(currentSection, index);
            return (
              <div key={key} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {index + 1}. {question}
                </label>
                <textarea
                  value={responses[key] || ''}
                  onChange={(e) => handleResponseChange(question, e.target.value, currentSection, index)}
                  placeholder="Share your experience here... (optional)"
                  className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 resize-vertical"
                />
              </div>
            );
          })}
        </div>

        {/* Section Actions */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => saveSection(currentSection)}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Section'}
            </Button>
            
            {savedSections.has(currentSection) && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                Saved
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const prevIndex = Math.max(0, currentIndex - 1);
                  setCurrentSection(sections[prevIndex]);
                }}
                disabled={currentIndex === 0}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <span className="text-sm text-muted-foreground px-2">
                {currentIndex + 1} of {sections.length}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const nextIndex = Math.min(sections.length - 1, currentIndex + 1);
                  setCurrentSection(sections[nextIndex]);
                }}
                disabled={currentIndex === sections.length - 1}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Final Actions */}
      <div className="mt-6 flex justify-center">
        <div className="space-y-4 text-center">
          <Button
            onClick={saveAllSections}
            disabled={isSaving}
            size="lg"
            className="flex items-center gap-2"
          >
            <Save className="h-5 w-5" />
            Save All & Complete BRAG Sheet
          </Button>
          
          <div className="flex gap-2 justify-center">
            <Button
              onClick={async () => {
                const { data, error } = await supabase.functions.invoke('debug-brag-dream-access', {
                  body: { userId: user?.id }
                });
                console.log('🔍 Manual debug results:', data);
                setDebugInfo(data);
                if (data?.fixes?.length > 0) {
                  toast.success(`Applied ${data.fixes.length} fixes!`);
                  setTimeout(() => window.location.reload(), 2000);
                }
              }}
              variant="outline"
              size="sm"
            >
              Debug Data Access
            </Button>
            
            <Button
              onClick={() => setBypassMode(!bypassMode)}
              variant="outline"
              size="sm"
            >
              {bypassMode ? 'Exit' : 'Enter'} Bypass Mode
            </Button>
          </div>
          
          {debugInfo && (
            <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left text-xs">
              <h4 className="font-bold mb-2">Debug Results:</h4>
              <pre className="whitespace-pre-wrap overflow-auto max-h-40">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Progress Summary */}
      {getCompletionPercentage() > 0 && (
        <div className="mt-6 bg-gray-50 rounded-xl p-4">
          <h4 className="font-medium mb-3">Your Progress</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sections.map((section) => {
              const sectionData = BRAG_QUESTIONS[section];
              const completion = getSectionCompletion(section);
              
              return (
                <div key={section} className="text-center">
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${sectionData.color} text-white flex items-center justify-center`}>
                    {React.cloneElement(sectionData.icon, { className: 'h-6 w-6' })}
                  </div>
                  <div className="text-sm font-medium">{sectionData.title}</div>
                  <div className="text-xs text-muted-foreground">{completion}% complete</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BragSheet;